<script setup>
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useTerraStore } from '~/terra/store/terra.store';

const emits = defineEmits(['close', 'saved']);

const { $services, $t, $toast, route } = useCommonImports();
const terra_store = useTerraStore();

// Deep-clone existing layers so edits don't mutate the store until saved.
const layers = ref(
  (terra_store.container?.properties?.arcgis?.layers || []).map(l => ({
    url: l.url || '',
    opacity: l.opacity ?? 0.85
  })),
);

const is_saving = ref(false);

function addLayer() {
  layers.value.push({ url: '', opacity: 0.85 });
}

function removeLayer(index) {
  layers.value.splice(index, 1);
}

async function onSave() {
  // Basic validation — each layer must have a URL.
  const invalid = layers.value.some(l => !l.url?.trim());
  if (invalid) {
    $toast({ type: 'error', title: $t('Validation error'), message: $t('All layers must have a URL.') });
    return;
  }

  is_saving.value = true;
  try {
    const container = terra_store.container;
    if (!container.properties) container.properties = {};
    if (!container.properties.arcgis) container.properties.arcgis = {};

    container.properties.arcgis.layers = layers.value.map(l => ({
      url: l.url.trim(),
      opacity: Number(l.opacity),
    }));

    const { data } = await $services.containers.patch({
      asset_id: route.params.asset_id,
      container_id: route.params.id,
      body: { container },
    });
    terra_store.container.properties = data.container.properties;
    emits('saved');
    emits('close');
  }
  catch (err) {
    logger.error('[ArcGIS layers popup] save error:', err);
    $toast({ type: 'error', title: $t('Something went wrong'), message: $t('Please try again later.') });
  }
  finally {
    is_saving.value = false;
  }
}
</script>

<template>
  <hawk-modal-container :width="640" content_class="rounded-lg max-w-[640px]">
    <div class="col-span-12">
      <!-- Header -->
      <hawk-modal-header class="!px-6 !py-4" @close="$emit('close')">
        <template #title>
          <div class="flex items-center gap-2">
            <icon-hawk-layers-three-two class="w-5 h-5 text-blue-600" />
            {{ $t('ArcGIS layers') }}
          </div>
        </template>
      </hawk-modal-header>

      <!-- Content -->
      <hawk-modal-content :is_scroll="false">
        <!-- Layer rows -->
        <div v-if="layers.length" class="flex flex-col gap-3 mb-4">
          <div
            v-for="(layer, i) in layers"
            :key="i"
            class="flex flex-col gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50"
          >
            <!-- Header row with delete button -->
            <div class="flex justify-between items-center">
              <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {{ $t('Layer details') }}
              </span>
              <button
                type="button"
                class="text-gray-400 hover:text-red-500 transition-colors"
                @click="removeLayer(i)"
              >
                <icon-hawk-trash-one class="w-4 h-4" />
              </button>
            </div>

            <!-- URL -->
            <div class="w-full">
              <label class="block text-xs font-medium text-gray-600 mb-1">
                {{ $t('Layer URL') }}
              </label>
              <input
                v-model="layer.url"
                type="url"
                placeholder="https://…/ImageServer"
                class="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
            </div>

            <!-- Opacity -->
            <div class="w-full">
              <label class="block text-xs font-medium text-gray-600 mb-1 flex justify-between items-center">
                <span>{{ $t('Opacity') }}</span>
                <span class="text-gray-500 font-semibold">{{ layer.opacity }}</span>
              </label>
              <input
                v-model.number="layer.opacity"
                type="range"
                min="0"
                max="1"
                step="0.01"
                class="w-full accent-blue-600 cursor-pointer"
              >
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-else
          class="text-sm text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-lg mb-4"
        >
          {{ $t('No layers added yet. Click "Add layer" to get started.') }}
        </div>

        <!-- Add layer button — max 1 layer allowed -->
        <hawk-button v-if="!layers.length" type="outlined" size="sm" @click="addLayer">
          <icon-hawk-plus-circle class="w-4 h-4 mr-1" />
          {{ $t('Add layer') }}
        </hawk-button>
      </hawk-modal-content>

      <!-- Footer -->
      <hawk-modal-footer class="flex justify-between items-center">
        <template #right>
          <div class="flex justify-end items-center gap-3">
            <hawk-button type="outlined" @click="$emit('close')">
              {{ $t('Cancel') }}
            </hawk-button>
            <hawk-button :loading="is_saving" @click="onSave">
              {{ $t('Save') }}
            </hawk-button>
          </div>
        </template>
      </hawk-modal-footer>
    </div>
  </hawk-modal-container>
</template>
